import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Plus, Search as SearchIcon, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ListTools } from "@/components/ListToolbar";
import { DataList, DetailPanel, DetailPlaceholder, type Column } from "@/components/DataList";
import { Badge } from "@/components/ui";
import { ImportPanel } from "@/features/import/ImportPanel";
import {
  inspectionTriggers,
  isActiveFilter,
  isTrigger,
  listPlans,
  planDetailQuery,
  planListQuery,
  qualityKeys,
  type ActiveFilter,
  type InspectionTrigger,
  type PlanListParams,
  type PlanRow,
} from "@/features/quality/api";
import { PlanPanel } from "@/features/quality/PlanPanel";
import { cn } from "@/lib/cn";
import { EXPORT_PAGE, type ExportColumn } from "@/lib/exportXlsx";
import { useFormat } from "@/lib/format";
import { useCan } from "@/lib/session";
import { inspectionPlanListColumns } from "@patronix/domain/list";
import { listView, viewQuery, viewSearch, type ViewSearch } from "@/lib/listView";

// Quality → Inspection plans: one row per plan (item + trigger + supplier),
// keyset (item code, trigger, id) through inspection_plan_page; detail = one
// document RPC, prefetched on hover. Bulk maintenance by import (ADR-034).

interface Search extends ViewSearch {
  trigger: InspectionTrigger | "all";
  active: ActiveFilter;
  q: string;
  id?: string | undefined;
  import?: true | undefined;
}

const invalidateAfterImport = [qualityKeys.all] as const;

export const Route = createFileRoute("/_app/quality/inspection-plans")({
  validateSearch: (raw: Record<string, unknown>): Search => {
    const s: Search = {
      trigger: isTrigger(raw.trigger) ? raw.trigger : "all",
      active: isActiveFilter(raw.active) ? raw.active : "active",
      q: typeof raw.q === "string" ? raw.q.slice(0, 100) : "",
    };
    if (typeof raw.id === "string" && raw.id) s.id = raw.id;
    if (raw.import === true || raw.import === "true") s.import = true;
    return { ...s, ...viewSearch(inspectionPlanListColumns, raw) };
  },
  loaderDeps: ({ search }) => ({ trigger: search.trigger, active: search.active, q: search.q, f: search.f, s: search.s }),
  loader: ({ context, deps }) => context.queryClient.ensureInfiniteQueryData(planListQuery(paramsOf(deps))),
  component: PlansPage,
});

function paramsOf(s: Pick<Search, "trigger" | "active" | "q" | "f" | "s">): PlanListParams {
  return { trigger: s.trigger === "all" ? null : s.trigger, active: s.active, q: s.q, ...viewQuery(inspectionPlanListColumns, s) };
}

function PlansPage() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const canManage = useCan("quality.plan.manage");
  const canImport = useCan("core.data.import") && canManage;
  const setSearch = useCallback(
    (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const params = paramsOf(search);
  const query = useInfiniteQuery(planListQuery(params));
  const [text, setText] = useState(search.q);
  useEffect(() => {
    if (text === search.q) return;
    const id = setTimeout(() => setSearch({ q: text, id: undefined }), 200);
    return () => clearTimeout(id);
  }, [text, search.q, setSearch]);

  const columns: Column<PlanRow>[] = [
    {
      key: "item",
      header: t("quality.plan.item"),
      width: "minmax(110px,0.8fr)",
      cell: (r, selected) => <span className={cn("font-mono text-xs", selected && "font-semibold text-accent-ink")}>{r.item_code}</span>,
    },
    {
      key: "description",
      header: t("stock.colDescription"),
      width: "minmax(180px,1.5fr)",
      cell: (r) => <span className={cn(!r.is_active && "text-ink-muted line-through decoration-ink-faint")}>{r.item_description}</span>,
    },
    {
      key: "trigger",
      header: t("quality.plan.trigger"),
      width: "minmax(130px,0.9fr)",
      cell: (r) => <Badge tone="neutral">{t(`quality.triggers.${r.trigger}`)}</Badge>,
      options: inspectionTriggers.map((v) => ({ value: v, label: t(`quality.triggers.${v}`) })),
    },
    {
      key: "supplier",
      header: t("quality.plan.supplier"),
      width: "minmax(110px,0.8fr)",
      className: "text-ink-soft",
      cell: (r) => (r.supplier_code ? <span className="font-mono text-xs">{r.supplier_code}</span> : t("quality.plan.anySupplier")),
    },
    {
      key: "sample",
      header: t("quality.plan.sampleSize"),
      width: "minmax(90px,0.6fr)",
      align: "end",
      cell: (r) => <span className="tabular">{r.sample_size === null ? t("quality.plan.all") : `${fmt.qty(String(r.sample_size))} ${r.base_uom_code}`}</span>,
    },
    {
      key: "lines",
      header: t("quality.plan.colLines"),
      width: "minmax(80px,0.5fr)",
      align: "end",
      cell: (r) => <span className="tabular">{r.line_count}</span>,
    },
  ];
  const exportColumns: ExportColumn<PlanRow>[] = [
    { header: "item_code", kind: "code", value: (r) => r.item_code },
    { header: t("stock.colDescription"), kind: "text", value: (r) => r.item_description },
    { header: "trigger", kind: "code", value: (r) => r.trigger },
    { header: "supplier", kind: "code", value: (r) => r.supplier_code },
    { header: "sample_size", kind: "number", value: (r) => (r.sample_size === null ? null : Number(r.sample_size)), sum: false },
    { header: t("quality.plan.colLines"), kind: "integer", value: (r) => r.line_count },
    { header: "is_active", kind: "boolean", value: (r) => r.is_active },
  ];
  const triggerFilters: (InspectionTrigger | "all")[] = ["all", ...inspectionTriggers];
  const pill = (on: boolean) =>
    cn(
      "h-8 rounded-full border px-3 text-[13px] transition-colors",
      on ? "border-ink bg-ink font-medium text-surface" : "border-border bg-surface text-ink hover:bg-surface-hover",
    );

  const listTools: ListTools<PlanRow> = {
    id: "inspection-plans",
    title: t("quality.plan.title"),
    onNew: canManage ? () => setSearch({ id: "new", import: undefined }) : undefined,
    export: { fileBase: "inspection-plans", sheet: t("quality.plan.title"), columns: exportColumns, fetchPage: (cursor, signal) => listPlans(params, cursor, signal, EXPORT_PAGE) },
    onImport: canImport ? () => setSearch({ import: true, id: undefined }) : undefined,
  };

  return (
    <div className="flex min-h-0 grow flex-col gap-4">
      <header className="flex flex-wrap items-end gap-3">
        <div className="flex grow flex-col gap-0.5">
          <nav aria-label="Breadcrumb" className="text-[13px] text-ink-muted">
            {t("nav.quality.title")} <span aria-hidden="true">/</span> {t("nav.quality.inspectionPlans")}
          </nav>
          <h1 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">{t("quality.plan.title")}</h1>
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => setSearch({ id: "new", import: undefined })}
            className="inline-flex h-10 items-center gap-1.5 rounded-xl bg-accent pl-3 pr-4 font-medium text-on-accent hover:bg-accent-hover"
          >
            <Plus size={18} strokeWidth={2} aria-hidden="true" />
            {t("quality.plan.new")}
          </button>
        )}
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <div role="group" aria-label={t("quality.plan.trigger")} className="flex flex-wrap gap-2">
          {triggerFilters.map((f) => (
            <button key={f} type="button" aria-pressed={search.trigger === f} onClick={() => setSearch({ trigger: f, id: undefined })} className={pill(search.trigger === f)}>
              {f === "all" ? t("po.filterAll") : t(`quality.triggers.${f}`)}
            </button>
          ))}
        </div>
        <select
          aria-label={t("quality.plan.activeFilter")}
          value={search.active}
          onChange={(e) => setSearch({ active: e.target.value as ActiveFilter, id: undefined })}
          className="h-8 rounded-full border border-border bg-surface px-3 text-[13px]"
        >
          <option value="active">{t("quality.plan.activeOnly")}</option>
          <option value="inactive">{t("quality.plan.inactiveOnly")}</option>
          <option value="all">{t("po.filterAll")}</option>
        </select>
        <div className="grow" />
        <label className="flex h-9 min-w-[240px] items-center gap-2 rounded-xl border border-border bg-surface px-3 text-ink-muted focus-within:outline-2 focus-within:outline-focus">
          <SearchIcon size={15} aria-hidden="true" />
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t("quality.plan.searchPlaceholder")}
            aria-label={t("quality.plan.searchPlaceholder")}
            data-list-search
            className="min-w-0 grow bg-transparent text-[13px] text-ink outline-none placeholder:text-ink-muted"
          />
          {text && (
            <button type="button" aria-label={t("po.clearFilters")} onClick={() => setText("")} className="text-ink-muted">
              <X size={14} />
            </button>
          )}
        </label>
      </div>

      <div className="flex min-h-0 grow gap-4">
        <DataList
          label={t("quality.plan.listLabel")}
          columns={columns}
          query={query}
          rowId={(r) => r.id}
          selectedId={search.id}
          onSelect={(r) => setSearch({ id: r.id, import: undefined })}
          onClear={() => setSearch({ id: undefined })}
          onPrefetch={(r) => void queryClient.prefetchQuery(planDetailQuery(r.id))}
          emptyTitle={t("quality.plan.emptyTitle")}
          emptyBody={t("quality.plan.emptyBody")}
          emptyAction={
            canManage ? (
              <button
                type="button"
                onClick={() => setSearch({ id: "new", import: undefined })}
                className="h-9 rounded-xl bg-accent px-3.5 text-[13px] font-medium text-on-accent hover:bg-accent-hover"
              >
                {t("quality.plan.new")}
              </button>
            ) : undefined
          }
          minWidth={760}
          tools={listTools}
          view={listView(inspectionPlanListColumns, search, setSearch)}
        />
        <DetailPanel
          label={search.import ? t("import.title") : t("quality.plan.detailLabel")}
          open={!!search.id || !!search.import}
          placeholder={<DetailPlaceholder title={t("quality.plan.selectPrompt")} hint={t("po.selectPromptHint")} />}
        >
          {search.import ? (
            <ImportPanel
              entities={["inspection_plans", "quality_characteristics"]}
              initial="inspection_plans"
              onClose={() => setSearch({ import: undefined })}
              invalidate={invalidateAfterImport}
            />
          ) : (
            search.id && <PlanPanel key={search.id} id={search.id} onClose={() => setSearch({ id: undefined })} onOpen={(id) => setSearch({ id })} />
          )}
        </DetailPanel>
      </div>
    </div>
  );
}
